import React, { useRef, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  Box,
  Backdrop,
  IconButton,
  Toolbar,
  Typography,
  Fade,
  useTheme,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  Close,
  Fullscreen,
  RestartAlt,
  PanTool,
} from '@mui/icons-material';

interface ZoomableCanvasProps {
  children: ReactNode;
  title?: string;
  maxZoom?: number;
  minZoom?: number;
  zoomStep?: number;
}

const ZoomableCanvas: React.FC<ZoomableCanvasProps> = ({
  children,
  title = 'Chart',
  maxZoom = 5,
  minZoom = 0.5,
  zoomStep = 0.2,
}) => {
  const theme = useTheme();
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomedContainerRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Handle zooming
  const handleZoomIn = useCallback(() => {
    setZoom(prev => {
      const newZoom = Math.min(prev + zoomStep, maxZoom);
      // Keep chart centered when zooming from 1x
      if (prev === 1) {
        setPan({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, [maxZoom, zoomStep]);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => {
      const newZoom = Math.max(prev - zoomStep, minZoom);
      // Reset pan when zooming back to 1x
      if (newZoom === 1) {
        setPan({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, [minZoom, zoomStep]);

  const handleResetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Handle fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!isZoomed) {
      setIsZoomed(true);
      // Reset zoom and center the chart when entering fullscreen
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setIsInitialized(false);
    } else {
      setIsZoomed(false);
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setIsInitialized(false);
    }
  }, [isZoomed]);

  // Handle mouse events for panning
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isZoomed || zoom <= 1) return;
    setIsDragging(true);
    setLastMousePos({ x: e.clientX, y: e.clientY });
    e.preventDefault();
  }, [isZoomed, zoom]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !isZoomed || zoom <= 1) return;

    const deltaX = e.clientX - lastMousePos.x;
    const deltaY = e.clientY - lastMousePos.y;

    setPan(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));

    setLastMousePos({ x: e.clientX, y: e.clientY });
    e.preventDefault();
  }, [isDragging, isZoomed, zoom, lastMousePos]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle wheel zoom
  const handleWheel = useCallback((e: WheelEvent) => {
    if (!isZoomed) return;
    
    e.preventDefault();
    const delta = e.deltaY > 0 ? -zoomStep : zoomStep;
    setZoom(prev => {
      const newZoom = Math.max(minZoom, Math.min(maxZoom, prev + delta));
      // Reset pan when zooming back to 1x with wheel
      if (newZoom === 1) {
        setPan({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, [isZoomed, maxZoom, minZoom, zoomStep]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isZoomed) return;

    switch (e.key) {
      case 'Escape':
        toggleFullscreen();
        break;
      case '=':
      case '+':
        e.preventDefault();
        handleZoomIn();
        break;
      case '-':
        e.preventDefault();
        handleZoomOut();
        break;
      case '0':
        e.preventDefault();
        handleResetZoom();
        break;
      default:
        break;
    }
  }, [isZoomed, toggleFullscreen, handleZoomIn, handleZoomOut, handleResetZoom]);

  // Add event listeners
  useEffect(() => {
    if (isZoomed) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('wheel', handleWheel, { passive: false });
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isZoomed, handleMouseMove, handleMouseUp, handleWheel, handleKeyDown]);

  // Prevent body scroll when zoomed
  useEffect(() => {
    if (isZoomed) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isZoomed]);

  // Ensure proper centering after component mounts in fullscreen
  useEffect(() => {
    if (isZoomed && !isInitialized && zoomedContainerRef.current) {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        // Force center position
        setPan({ x: 0, y: 0 });
        setZoom(1);
        setIsInitialized(true);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isZoomed, isInitialized]);

  const normalView = (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        transition: 'transform 0.2s ease',
        '&:hover': {
          transform: 'scale(1.02)',
        },
      }}
      onClick={toggleFullscreen}
    >
      {children}
      
      {/* Hover overlay with zoom icon */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          transition: 'all 0.3s ease',
          borderRadius: '16px',
          '&:hover': {
            background: 'rgba(0, 0, 0, 0.1)',
            opacity: 1,
          },
        }}
      >
        <Box
          sx={{
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: '50%',
            width: 64,
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: `2px solid ${theme.palette.primary.main}`,
          }}
        >
          <Fullscreen 
            sx={{ 
              fontSize: 32, 
              color: theme.palette.primary.main 
            }} 
          />
        </Box>
      </Box>
    </Box>
  );

  const fullscreenView = (
    <Backdrop
      open={isZoomed}
      sx={{
        zIndex: theme.zIndex.modal + 1,
        bgcolor: 'rgba(0, 0, 0, 0.95)',
      }}
    >
      <Fade in={isZoomed}>
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Toolbar */}
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              top: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: theme.zIndex.modal + 2,
              borderRadius: '24px',
              background: theme.palette.mode === 'dark'
                ? 'rgba(30, 30, 30, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            }}
          >
            <Toolbar
              sx={{
                gap: 1,
                px: 3,
                py: 1,
                minHeight: '64px !important',
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  flexGrow: 1, 
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {title}
              </Typography>

              <Tooltip title="Vergrößern (+)" arrow>
                <IconButton
                  onClick={handleZoomIn}
                  disabled={zoom >= maxZoom}
                  sx={{
                    bgcolor: theme.palette.primary.main + '20',
                    '&:hover': { bgcolor: theme.palette.primary.main + '40' },
                  }}
                >
                  <ZoomIn />
                </IconButton>
              </Tooltip>

              <Tooltip title="Verkleinern (-)" arrow>
                <IconButton
                  onClick={handleZoomOut}
                  disabled={zoom <= minZoom}
                  sx={{
                    bgcolor: theme.palette.primary.main + '20',
                    '&:hover': { bgcolor: theme.palette.primary.main + '40' },
                  }}
                >
                  <ZoomOut />
                </IconButton>
              </Tooltip>

              <Tooltip title="Zurücksetzen (0)" arrow>
                <IconButton
                  onClick={handleResetZoom}
                  sx={{
                    bgcolor: theme.palette.secondary.main + '20',
                    '&:hover': { bgcolor: theme.palette.secondary.main + '40' },
                  }}
                >
                  <RestartAlt />
                </IconButton>
              </Tooltip>

              <Tooltip title={zoom > 1 ? "Ziehen zum Verschieben" : "Vergrößern zum Verschieben"} arrow>
                <IconButton
                  sx={{
                    bgcolor: zoom > 1 ? theme.palette.success.main + '20' : theme.palette.grey[300],
                    cursor: zoom > 1 ? 'grab' : 'default',
                    '&:hover': { 
                      bgcolor: zoom > 1 ? theme.palette.success.main + '40' : theme.palette.grey[400],
                    },
                  }}
                >
                  <PanTool />
                </IconButton>
              </Tooltip>

              <Box sx={{ mx: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Zoom: {Math.round(zoom * 100)}%
                </Typography>
              </Box>

              <Tooltip title="Schließen (Esc)" arrow>
                <IconButton
                  onClick={toggleFullscreen}
                  sx={{
                    bgcolor: theme.palette.error.main + '20',
                    '&:hover': { bgcolor: theme.palette.error.main + '40' },
                  }}
                >
                  <Close />
                </IconButton>
              </Tooltip>
            </Toolbar>
          </Paper>

          {/* Chart container */}
          <Box
            ref={zoomedContainerRef}
            sx={{
              position: 'fixed',
              top: '100px',
              left: 0,
              right: 0,
              bottom: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
            onMouseDown={handleMouseDown}
          >
            <Box
              sx={{
                transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                transformOrigin: 'center center',
                transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                width: 'fit-content',
                height: 'fit-content',
                maxWidth: '90vw',
                maxHeight: '80vh',
                margin: 'auto',
                display: 'block',
                // Ensure all child elements are properly centered
                '& > *': {
                  display: 'block',
                  margin: '0 auto',
                  maxWidth: '100%',
                  height: 'auto',
                },
                // Special handling for Canvas elements
                '& canvas': {
                  display: 'block',
                  margin: '0 auto',
                  maxWidth: '100%',
                  height: 'auto',
                },
                // Special handling for Box components containing charts
                '& > div': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                },
              }}
            >
              {children}
            </Box>
          </Box>

          {/* Instructions */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: theme.zIndex.modal + 2,
            }}
          >
            <Paper
              elevation={4}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: '16px',
                background: theme.palette.mode === 'dark'
                  ? 'rgba(30, 30, 30, 0.9)'
                  : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                Mausrad: Zoomen • {zoom > 1 ? 'Ziehen: Verschieben' : 'Vergrößern um zu verschieben'} • +/-: Zoom • 0: Reset • Esc: Schließen
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Fade>
    </Backdrop>
  );

  return (
    <>
      {normalView}
      {isZoomed && fullscreenView}
    </>
  );
};

export default ZoomableCanvas;